<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

export interface SelectorOption {
  [key: string]: string | number;
}

interface Props {
  modelValue: string | number | null;
  options: (string | number | SelectorOption)[];
  label?: string;
  placeholder?: string;
  optionLabel?: string;
  optionValue?: string;
  error?: boolean;
  errorMessage?: string;
  containerClass?: string;
  formatSelected?: (value: string | number | null) => string;
  formatOption?: (option: string | number | SelectorOption) => string;
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  placeholder: 'Select option',
  optionLabel: 'label',
  optionValue: 'value',
  error: false,
  errorMessage: '',
  containerClass: 'bg-black/20',
  formatSelected: undefined,
  formatOption: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
}>();

const isDropdownOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const getOptionLabel = (option: string | number | SelectorOption) => {
  if (props.formatOption) return props.formatOption(option);
  if (typeof option === 'object' && option !== null) {
    return option[props.optionLabel];
  }
  return String(option);
};

const getOptionValue = (option: string | number | SelectorOption) => {
  if (typeof option === 'object' && option !== null) {
    return option[props.optionValue];
  }
  return option;
};

const selectedLabel = computed(() => {
  if (
    props.modelValue === null ||
    props.modelValue === undefined ||
    props.modelValue === ''
  ) {
    return props.placeholder;
  }

  if (props.formatSelected) {
    return props.formatSelected(props.modelValue);
  }

  const selected = props.options.find(
    (opt) => getOptionValue(opt) === props.modelValue,
  );

  if (selected !== undefined) {
    return getOptionLabel(selected);
  }

  return String(props.modelValue);
});

const selectOption = (option: string | number | SelectorOption) => {
  emit('update:modelValue', getOptionValue(option));
  isDropdownOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="space-y-2">
    <label v-if="label" class="text-sm font-semibold text-white">{{
      label
    }}</label>
    <div ref="dropdownRef" class="relative">
      <button
        type="button"
        class="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-sm text-white outline-none transition focus:border-primary/60"
        :class="[
          error
            ? 'border-red-500 bg-red-500/10'
            : `border-white/10 hover:border-white/20 ${containerClass}`,
        ]"
        @click="toggleDropdown"
      >
        <span
          class="truncate font-medium"
          :class="
            modelValue === null || modelValue === undefined || modelValue === ''
              ? 'text-white/40'
              : 'text-white'
          "
        >
          {{ selectedLabel }}
        </span>
        <div
          class="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 transition-transform duration-200"
          :class="{ 'rotate-180': isDropdownOpen }"
        >
          <IconifyIcon icon="ph:caret-down-bold" class="size-4" />
        </div>
      </button>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <div
          v-if="isDropdownOpen"
          class="absolute left-0 z-50 mt-2 max-h-[220px] w-full overflow-y-auto rounded-xl border border-white/10 bg-[#16171D] p-1 shadow-2xl scrollbar-thin scrollbar-thumb-white/10"
        >
          <button
            v-for="(option, index) in options"
            :key="index"
            type="button"
            class="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm transition-all hover:bg-white/5"
            :class="[
              modelValue === getOptionValue(option)
                ? 'bg-primary/10 font-bold text-primary'
                : 'text-white/70',
            ]"
            @click="selectOption(option)"
          >
            <div
              class="flex size-4 shrink-0 items-center justify-center rounded-full border"
              :class="[
                modelValue === getOptionValue(option)
                  ? 'border-primary'
                  : 'border-white/20',
              ]"
            >
              <div
                v-if="modelValue === getOptionValue(option)"
                class="size-2 rounded-full bg-primary"
              ></div>
            </div>
            {{ getOptionLabel(option) }}
          </button>
        </div>
      </transition>
    </div>
    <p v-if="error && errorMessage" class="ml-1 mt-1 text-xs text-red-400">
      {{ errorMessage }}
    </p>
  </div>
</template>
